import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { couponService } from '../../services/couponService';
import fiscalPrinterService from '../../services/fiscalPrinterService';
import BaseModal from '../common/BaseModal';

const CouponPurchaseDocumentModal = ({ couponId, isOpen, onClose }) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fiscalPrinting, setFiscalPrinting] = useState(false);
  useEffect(() => {
    if (isOpen && couponId) {
      fetchDocument();
    }
  }, [isOpen, couponId]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await couponService.getCouponDocument(couponId);
      if (response.success && response.data) {
        setDocument(response.data);
      } else {
        setError(response.message || 'Nie znaleziono dokumentu');
      }
    } catch (err) {
      console.error('Błąd pobierania dokumentu:', err);
      setError('Błąd pobierania dokumentu zakupu');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const response = await api.get(`/coupons/${couponId}/document/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Blad generowania PDF dokumentu kuponu.');
    }
  };

  // Drukowanie na drukarce fiskalnej (dokument niefiskalny)
  const handleFiscalPrint = async () => {
    if (!document) return;

    setFiscalPrinting(true);

    try {
      // Drukarka fiskalna wymaga prostego tekstu bez polskich znaków
      const removeDiacritics = (str) => {
        return str
          .replace(/ą/g, 'a').replace(/Ą/g, 'A')
          .replace(/ć/g, 'c').replace(/Ć/g, 'C')
          .replace(/ę/g, 'e').replace(/Ę/g, 'E')
          .replace(/ł/g, 'l').replace(/Ł/g, 'L')
          .replace(/ń/g, 'n').replace(/Ń/g, 'N')
          .replace(/ó/g, 'o').replace(/Ó/g, 'O')
          .replace(/ś/g, 's').replace(/Ś/g, 'S')
          .replace(/ź/g, 'z').replace(/Ź/g, 'Z')
          .replace(/ż/g, 'z').replace(/Ż/g, 'Z');
      };

      const couponCode = document.coupon?.code || '-';
      const externalCode = document.coupon?.external_code || null;
      const couponValue = parseFloat(document.coupon?.value || 0).toFixed(2);
      const documentNumber = document.document_number || '-';
      // Formatuj daty
      const expiryDate = document.coupon?.expiry_date
        ? new Date(document.coupon.expiry_date).toLocaleDateString('pl-PL') + ' ' + new Date(document.coupon.expiry_date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
        : '-';
      const purchaseDate = document.created_at
        ? new Date(document.created_at).toLocaleDateString('pl-PL') + ' ' + new Date(document.created_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
        : '-';
      const locationName = document.location?.name || 'V3SYS';

      // Protokół XML - bez limitu linii!
      // Używamy obiektów z formatowaniem: { text, center, high, underline }
      const lines = [
        // Numer dokumentu KP
        { text: `Nr: ${documentNumber}`, center: true },
        { spacer: true },
        // Kod kuponu - duża czcionka
        { text: 'KOD KUPONU:', center: true },
        { text: couponCode, center: true, big: true },
        { spacer: true },
        // Kod zewnętrzny (jeśli istnieje)
        ...(externalCode ? [
          { text: 'KOD ZEWNETRZNY:', center: true },
          { text: externalCode, center: true, big: true },
          { spacer: true }
        ] : []),
        // Wartość - duża czcionka
        { text: `WARTOSC: ${couponValue} zl`, center: true, big: true },
        { underlineBefore: true, spacer: true },
        // Data zakupu
        { text: `Data zakupu: ${removeDiacritics(purchaseDate)}` },
        // Data ważności
        { text: `Wazny do: ${expiryDate}` },
        { underlineBefore: true, spacer: true },
        // Podziękowanie z lokalizacją
        { text: 'Dziekujemy za zakup', center: true },
        { text: `w sklepie ${removeDiacritics(locationName)}`, center: true },
        { spacer: true },
        { text: 'Zachowaj ten dokument', center: true },
      ];

      // Wyślij do druku przez Web Serial API
      const result = await fiscalPrinterService.printNonFiscalDocument({
        title: removeDiacritics('POTWIERDZENIE ZAKUPU'),
        titleBig: false,
        lines: lines
      });

      if (result.success) {
        toast.success('Dokument wysłany do drukarki fiskalnej!');
      } else {
        toast.error(`Błąd druku: ${result.error}`);
      }
    } catch (err) {
      console.error('Błąd drukowania fiskalnego:', err);
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setFiscalPrinting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPaymentMethod = (method) => {
    const methods = {
      'gotowka': 'Gotówka',
      'karta': 'Karta płatnicza',
      'przelew': 'Przelew bankowy',
      'blik': 'BLIK'
    };
    return methods[method] || method || '-';
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="📄 Dokument zakupu kuponu"
      size="md"
      zIndex={1000}
      headerStyle={{
        backgroundColor: 'var(--bg)',
        color: 'var(--text)',
        borderBottom: '1px solid var(--border)',
      }}
      footer={
        document && !loading ? (
          <>
            <button
              onClick={handlePrint}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--info)', color: 'var(--info)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              🖨️ Drukuj (przeglądarka)
            </button>
            <button
              onClick={handleFiscalPrint}
              disabled={fiscalPrinting}
              style={{
                padding: '6px 12px',
                border: `1px solid ${fiscalPrinting ? 'var(--text-muted)' : 'var(--success)'}`,
                color: fiscalPrinting ? 'var(--text-muted)' : 'var(--success)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {fiscalPrinting ? '⏳ Drukowanie...' : '🧾 Drukuj (kasa fiskalna)'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
                fontSize: '12px'
              }}
            >
              Zamknij
            </button>
          </>
        ) : undefined
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>
          Ładowanie dokumentu...
        </div>
      )}

      {error && (
        <div style={{
          padding: 15,
          background: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: 6,
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {document && !loading && (
        <div
          style={{
            background: "var(--card-bg)",
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 20,
            fontFamily: "'Courier New', monospace"
          }}
        >
          {/* Nagłówek dokumentu */}
          <div className="header" style={{ textAlign: 'center', borderBottom: '1px dashed var(--border)', paddingBottom: 10, marginBottom: 10 }}>
            <div className="title" style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 5 }}>
              POTWIERDZENIE ZAKUPU KUPONU
            </div>
            <div className="doc-number" style={{ fontSize: 14, fontWeight: 'bold', color: 'var(--info)' }}>
              {document.document_number}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 5 }}>
              {formatDate(document.created_at)}
            </div>
          </div>

          {/* Kod kuponu */}
          <div className="coupon-code" style={{
            textAlign: 'center',
            fontSize: 28,
            fontWeight: 'bold',
            letterSpacing: 4,
            padding: 15,
            background: 'var(--info-light)',
            border: '2px dashed var(--info)',
            borderRadius: 8,
            margin: '15px 0',
            color: 'var(--info)'
          }}>
            {document.coupon?.code}
          </div>

          {/* Kod zewnętrzny (jeśli istnieje) */}
          {document.coupon?.external_code && (
            <div style={{
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 'bold',
              letterSpacing: 2,
              padding: 10,
              background: 'var(--warning-light)',
              border: '1px dashed var(--warning)',
              borderRadius: 6,
              margin: '10px 0',
              color: 'var(--warning)'
            }}>
              <div style={{ fontSize: 10, fontWeight: 'normal', marginBottom: 3 }}>Kod zewnętrzny:</div>
              {document.coupon.external_code}
            </div>
          )}

          {/* Wartość */}
          <div className="amount" style={{
            textAlign: 'center',
            fontSize: 22,
            fontWeight: 'bold',
            color: 'var(--success)',
            margin: '15px 0'
          }}>
            Wartość: {parseFloat(document.coupon?.value || 0).toFixed(2)} zł
          </div>

          {/* Szczegóły */}
          <div className="section" style={{ margin: '15px 0', padding: '10px 0', borderTop: '1px dashed var(--border)', borderBottom: '1px dashed var(--border)' }}>
            <div className="row" style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
              <span className="label" style={{ color: 'var(--text-secondary)' }}>Ważny do:</span>
              <span className="value" style={{ fontWeight: 'bold' }}>{document.coupon?.expiry_date}</span>
            </div>
            <div className="row" style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
              <span className="label" style={{ color: 'var(--text-secondary)' }}>Status:</span>
              <span className="value" style={{ fontWeight: 'bold', color: document.coupon?.status === 'aktywny' ? 'var(--success)' : 'var(--danger)' }}>
                {document.coupon?.status?.toUpperCase()}
              </span>
            </div>
            <div className="row" style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
              <span className="label" style={{ color: 'var(--text-secondary)' }}>Płatność:</span>
              <span className="value" style={{ fontWeight: 'bold' }}>{formatPaymentMethod(document.payment?.method)}</span>
            </div>
          </div>

          {/* Dane klienta */}
          {(document.customer?.name || document.customer?.phone) && (
            <div className="section" style={{ margin: '10px 0', padding: '10px 0', borderBottom: '1px dashed var(--border)' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 5, fontSize: 12 }}>Dane klienta:</div>
              {document.customer?.name && (
                <div className="row" style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                  <span className="label" style={{ color: 'var(--text-secondary)' }}>Imię:</span>
                  <span className="value">{document.customer.name}</span>
                </div>
              )}
              {document.customer?.phone && (
                <div className="row" style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                  <span className="label" style={{ color: 'var(--text-secondary)' }}>Telefon:</span>
                  <span className="value">{document.customer.phone}</span>
                </div>
              )}
            </div>
          )}

          {/* Lokalizacja */}
          {document.location?.name && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 10 }}>
              Punkt sprzedaży: {document.location.name}
            </div>
          )}

          {/* Sprzedawca */}
          {document.seller && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center' }}>
              Sprzedawca: {document.seller}
            </div>
          )}

          {/* Stopka */}
          <div className="footer" style={{
            textAlign: 'center',
            fontSize: 10,
            marginTop: 15,
            paddingTop: 10,
            borderTop: '1px dashed var(--border)',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 5 }}>Dziękujemy za zakup!</div>
            <div className="terms" style={{ fontSize: 9 }}>
              Kupon jest ważny do daty wskazanej powyżej.<br/>
              Prosimy zachować ten dokument jako potwierdzenie zakupu.
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default CouponPurchaseDocumentModal;
